import { Component, inject, input, TemplateRef, ViewChild } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { CalendarRole } from '../../models/calendar-role';
import { BranchInfo } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';
import { CalendarService } from '../../services/calendar.service';
import { BranchSelectorComponent } from '../branch-selector/branch-selector.component';
import { ForemanSelectorComponent } from '../foreman-selector/foreman-selector.component';
import {
  InstallationAbilitySelectorComponent,
} from '../installation-ability-selector/installation-ability-selector.component';
import { RoleSelectorComponent } from '../role-selector/role-selector.component';

@Component({
  selector: 'app-side-modal',
  imports: [
    BranchSelectorComponent,
    ForemanSelectorComponent,
    RoleSelectorComponent,
    InstallationAbilitySelectorComponent,
  ],
  templateUrl: './side-modal.component.html',
})
export class SideModalComponent {
  showForemanSelector = input(true);
  @ViewChild('sideModal') sideModalTemplate!: TemplateRef<unknown>;
  @ViewChild(BranchSelectorComponent) branchSelector?: BranchSelectorComponent;
  @ViewChild(ForemanSelectorComponent) foremanSelector?: ForemanSelectorComponent;
  @ViewChild(RoleSelectorComponent) roleSelector?: RoleSelectorComponent;
  @ViewChild(InstallationAbilitySelectorComponent) abilitySelector?: InstallationAbilitySelectorComponent;
  private readonly offcanvasService = inject(NgbOffcanvas);
  private readonly branchService = inject(BranchService);
  private readonly calendarService = inject(CalendarService);

  pendingBranch: BranchInfo | null = null;
  pendingForemenIds: string[] | null = null;
  pendingRoles: CalendarRole[] | null = null;
  pendingAbilities: string[] | null = null;
  effectiveBranchId: string | null = null;

  openSettings(): void {
    this.resetPendingState();
    this.effectiveBranchId = this.branchService.getCurrentSelectedBranch()?.id ?? null;
    // Seed pending roles to the currently-applied state so the foreman-selector
    // opens with the right filtered list on first paint (display-only; does not
    // go through the apply path until Apply is clicked).
    this.pendingRoles = [...this.calendarService.getCurrentSelectedRoles()];
    this.offcanvasService.open(this.sideModalTemplate, {
      ariaLabelledBy: 'calendar-settings',
      position: 'end',
    });
  }

  open(content: TemplateRef<unknown>) {
    this.offcanvasService.open(content, {
      ariaLabelledBy: 'calendar-settings',
      position: 'end',
    });
  }

  onBranchSelected(selectedBranch: BranchInfo) {
    this.pendingBranch = selectedBranch;
    this.effectiveBranchId = selectedBranch.id ?? null;
    this.pendingForemenIds = null;
    this.pendingRoles = null;
    this.pendingAbilities = null;
    this.foremanSelector?.resetSelection();
    this.roleSelector?.resetSelection();
    this.abilitySelector?.resetSelection();
  }

  onForemenSelected(ids: string[]): void {
    this.pendingForemenIds = ids;
  }

  onRolesSelected(roles: CalendarRole[]): void {
    this.pendingRoles = roles;
  }

  onAbilitiesSelected(abilities: string[]): void {
    this.pendingAbilities = abilities;
  }

  applySettings(): void {
    if (this.pendingBranch) {
      this.branchService.setSelectedBranch(this.pendingBranch);
      // Branch change resets all per-branch filters — they don't carry over
      // across branches, so clear them at the source rather than rely on
      // each selector's local state.
      this.calendarService.clearSelectedForemen();
      this.calendarService.resetSelectedRolesToDefault();
      this.calendarService.clearSelectedAbilities();
    }
    if (this.showForemanSelector() && this.pendingForemenIds !== null) {
      // Trim selections to IDs whose personnel are visible under the pending
      // roles. This handles the case where the user narrowed a role inside the
      // modal and is applying with stale name picks that no longer belong to
      // any active role. If the foreman-selector isn't mounted, pass as-is.
      const validIds = this.foremanSelector
        ? this.pendingForemenIds.filter((id) =>
            this.foremanSelector!.foremen.some((f) => f.id === id),
          )
        : this.pendingForemenIds;
      this.calendarService.setSelectedForemenIds(validIds);
    }
    if (this.pendingRoles !== null) {
      this.calendarService.setSelectedRoles(this.pendingRoles);
    }
    if (this.pendingAbilities !== null) {
      this.calendarService.setSelectedAbilities(this.pendingAbilities);
    }
    this.offcanvasService.dismiss('Settings applied');
  }

  resetToDefault(): void {
    this.resetPendingState();
    this.effectiveBranchId = null;
    this.branchSelector?.resetSelection();
    if (this.showForemanSelector()) {
      this.foremanSelector?.resetSelection();
    }
    this.roleSelector?.resetSelection();
    this.abilitySelector?.resetSelection();
    this.branchService.resetToDefaultBranch();
    this.calendarService.clearSelectedForemen();
    // Roles default back to ['Foreman'] (per AC1) — clearing would hide
    // every non-visiting crew on a fresh open.
    this.calendarService.resetSelectedRolesToDefault();
    this.calendarService.clearSelectedAbilities();
  }

  private resetPendingState(): void {
    this.pendingBranch = null;
    this.pendingForemenIds = null;
    this.pendingRoles = null;
    this.pendingAbilities = null;
  }
}
